import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { type SuccessResponse, zodSuccessResponseSchema } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  zodDeleteCompanyAliasAddressEndpointSchema,
  zodDeleteCompanyAliasAddressSearchParamsSchema,
} from './validations';

/**
 * Deletes a company alias address.
 *
 * @param aliasAddressId - The ID of the company alias address to delete.
 * @param issuerId - Optional issuer ID to remove only the restriction of the alias address to the given issuer instead of deleting the alias address.
 * @returns - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company-Alias-Addresses/operation/delete_company_address_vadmin_company_alias_address__company_address_uuid__delete
 */
export const deleteCompanyAliasAddress = async ({
  aliasAddressId,
  issuerId,
}: {
  aliasAddressId: UUID;
  issuerId?: UUID;
}) => {
  try {
    return await vestigasApi
      .delete(ENDPOINT.COMPANY.ALIAS_ADDRESS.DELETE(aliasAddressId), {
        searchParams: issuerId ? { issuerId } : undefined,
        validate: {
          pathSegments: zodDeleteCompanyAliasAddressEndpointSchema,
          response: zodSuccessResponseSchema,
          searchParams: zodDeleteCompanyAliasAddressSearchParamsSchema,
        },
      })
      .json<SuccessResponse>();
  } catch (error) {
    log.error({
      message: 'Error deleting company alias address',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 404: {
          errorMessage = i18n.t(
            ($) => $.company.deleteCompanyAliasAddress.error404,
            {
              ns: 'data',
            },
          );
          break;
        }

        case 422: {
          errorMessage = i18n.t(
            ($) => $.company.deleteCompanyAliasAddress.error422,
            {
              ns: 'data',
            },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.company.deleteCompanyAliasAddress.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
